import { Component, ElementRef, HostListener, Input } from "@angular/core";
import { DeckLayerName, PonderDataClient } from "@apiserver-rest/ponder-data-client";
import { DataResponse, Deck, DeckCard, DeckCardChanges, DeckCardTagType, DeckChanges, UpdateDeckRequest, UpdateDeckResponse } from "@apiserver-rest/ponder-data-client-base";
import { MagicDeckRowComponent } from "./magic-deck-row.component";
import { EventBroker } from "@ponder-core/events/eventBroker";
import { take } from "rxjs";
import { isMobileScreen } from "@ponder-shared/utils/screenBreakpoints";
import { environment } from "../../../../environments/environment";
import { DeckBaseComponent } from "@ponder-features/user/decks/deck/deck-base.component";
import { DeckLayerComponent } from "@ponder-features/user/decks/deck/deck-layer.component";
import { getMagicCardRoute } from "../card/magicCardRouter";
import { FormatCurrencyPipe } from "@ponder-shared/pipes/format-currency.pipe";
import { NavigationService } from "@ponder-core/navigation/navigation.service";
import { SvgIconComponent } from "@ponder-shared/components/svg-icon.component";
import { SvgIcon } from "@ponder-shared/utils/svgIcons";

@Component({
    selector: "magic-deck-card-dialog",
    imports: [FormatCurrencyPipe, SvgIconComponent],
    templateUrl: "./magic-deck-card-dialog.component.html"
})
export class MagicDeckCardDialogComponent {
    @Input({ required: true }) deck!: Deck;
    @Input({ required: true }) deckComponent!: DeckBaseComponent;
    @Input({ required: true }) deckLayerComponent!: DeckLayerComponent;
    @Input({ required: true }) deckCard!: DeckCard;
    @Input({ required: true }) deckRow!: MagicDeckRowComponent;
    @Input({ required: true }) isOwnDeck: boolean = false;

    public readonly closeSvgIcon: SvgIcon = SvgIcon.Close;
    public isProcessing: boolean = false;
    public cardUrl!: string;

    public constructor(
        private elementRef: ElementRef<HTMLElement>, 
        private ponderDataClient: PonderDataClient, 
        private events: EventBroker,
        private navigation: NavigationService) {
        
    }

    public ngOnInit(): void {
        this.cardUrl = getMagicCardRoute(this.deckCard.card.item.data!.magic!);
    }

    private closeDialog(): void {
        this.deckRow.displayRowEdit = false;
    }

    // -------------------------------------------------------------------------------------
    // Global Events
    // -------------------------------------------------------------------------------------

    @HostListener("document:keydown.escape", ["$event"]) 
    private onEscapePressed(event: KeyboardEvent): void {
        this.closeDialog();
    }

    @HostListener("document:mouseup", ["$event"])
    private onGlobalMouseUp(event: MouseEvent): void {
        // Did the user click outside of our modal panel?
        if (!this.elementRef.nativeElement.contains((event.target as HTMLInputElement)) && this.deckRow.displayRowEdit) {
            this.closeDialog();
        }
    }
    
    // -------------------------------------------------------------------------------------
    // Layer Transitions
    // -------------------------------------------------------------------------------------

    public showMoveToMainboard(): boolean {
        return (this.deckRow.deckLayerComponent.name != DeckLayerName.Mainboard);
    }

    public moveToMainboardClicked(): void {
        this.deckComponent.moveCardToLayer(this.deckCard, this.deckRow.deckLayerComponent.name, DeckLayerName.Mainboard);
    }

    public showMoveToSideboard(): boolean {
        return (this.deckRow.deckLayerComponent.name != DeckLayerName.Sideboard);
    }

    public moveToSideboardClicked(): void {
        this.deckComponent.moveCardToLayer(this.deckCard, this.deckRow.deckLayerComponent.name, DeckLayerName.Sideboard);
    }

    public showMoveToMaybeboard(): boolean {
        return (this.deckRow.deckLayerComponent.name != DeckLayerName.Maybeboard);
    }

    public moveToMaybeboardClicked(): void {
        this.deckComponent.moveCardToLayer(this.deckCard, this.deckRow.deckLayerComponent.name, DeckLayerName.Maybeboard);
    }

    public showMoveToPlanning(): boolean {
        return (this.deckRow.deckLayerComponent.name != DeckLayerName.Planning);
    }

    public moveToPlanningClicked(): void {
        this.deckComponent.moveCardToLayer(this.deckCard, this.deckRow.deckLayerComponent.name, DeckLayerName.Planning);
    }

    // -------------------------------------------------------------------------------------
    // Commander Functionality
    // -------------------------------------------------------------------------------------

    public isCommander(): boolean {
        return this.deckCard.tag == DeckCardTagType.Commander;
    }

    public canBeCommander(): boolean {
        if (this.isCommander()) {
            return false;
        }

        if (this.deck.type !== "Commander") {
            return false;
        }

        var magicCard = this.deckCard.card.item.data?.magic;

        if (!magicCard) {
            return false;
        }

        if ((magicCard.faces![0].typeLine?.includes("Legendary") ?? false) &&
            (magicCard.faces![0].typeLine?.includes("Creature") ?? false)) {
            return true;
        } else if (magicCard.faces![0].typeLine?.includes("Planeswalker") ?? false) {
            return magicCard.faces![0].oracleText?.includes("can be your commander") ?? false;
        } else {
            return false;
        }
    }

    public setAsCommanderClicked(): void {
        this.updateCardTag(DeckCardTagType.Commander);
    }

    public removeAsCommanderClicked(): void {
        this.updateCardTag(DeckCardTagType.None);
    }
    
    // -------------------------------------------------------------------------------------
    // Oathbreaker Functionality
    // -------------------------------------------------------------------------------------

    public isOathbreaker(): boolean {
        return this.deckCard.tag == DeckCardTagType.Oathbreaker;
    }

    public canBeOathbreaker(): boolean {
        if (this.isOathbreaker()) {
            return false;
        }

        if (this.deck.type !== "Oathbreaker") {
            return false;
        }

        var magicCard = this.deckCard.card.item.data?.magic;

        if (!magicCard) {
            return false;
        }

        return magicCard.faces![0].typeLine!.includes("Planeswalker");
    }

    public isSignatureSpell(): boolean {
        return this.deckCard.tag == DeckCardTagType.SignatureSpell;
    }

    public canBeSignatureSpell(): boolean {
        if (this.isSignatureSpell()) {
            return false;
        }

        if (this.deck.type !== "Oathbreaker") {
            return false;
        }

        var magicCard = this.deckCard.card.item.data?.magic;

        if (!magicCard) {
            return false;
        }

        return magicCard.faces![0].typeLine!.includes("Instant") ||
               magicCard.faces![0].typeLine!.includes("Sorcery");
    }

    public setAsOathbreakerClicked(): void {
        this.updateCardTag(DeckCardTagType.Oathbreaker);
    }

    public removeAsOathbreakerClicked(): void {
        this.updateCardTag(DeckCardTagType.None);
    }

    public setAsSignatureSpellClicked(): void {
        this.updateCardTag(DeckCardTagType.SignatureSpell);
    }

    public removeAsSignatureSpellClicked(): void {
        this.updateCardTag(DeckCardTagType.None);
    }
    
    // -------------------------------------------------------------------------------------
    // Mobile
    // -------------------------------------------------------------------------------------

    public isMobileScreen(): boolean {
        return isMobileScreen();
    }

    public getCardImageUrl(): string {
        return `${environment.ponderImageBaseUrl}/card/medium/front/${this.deckCard.card.item!.id!}.jpg`;
    }

    public hasPrice(): boolean {
        return (this.deckCard?.card?.price.normalUSD ? true : false);
    }

    public hasSpecialPrice(): boolean {
        return (this.deckCard?.card?.price.specialUSD ? true : false);
    }

    public onCardImageClicked(): void {
        this.navigation.goto(this.deckRow.getUrl());
    }

    public getCollectedCount(): string {
        var count = this.deckCard.card.collected?.count ?? 0;
        return count ? `${count}` : "";
    }

    public getSpecialCollectedCount(): string {
        var count = this.deckCard.card.collected?.specialCount ?? 0;
        return count ? `${count}` : "";
    }
    
    // -------------------------------------------------------------------------------------
    // Misc
    // -------------------------------------------------------------------------------------

    public switchVersionClicked(): void {
        this.events.deck.displaySwitchCardVersionEvent.emit(this.deckCard);
        this.closeDialog();
    }

    public addVersionClicked(): void {
        this.events.deck.displayAddAlternateCardVersionEvent.emit(this.deckCard);
        this.closeDialog();
    }

    public setAsBackgroundClicked(): void {
        if (this.isProcessing) {
            return;
        }

        this.isProcessing = true;

        var request = new UpdateDeckRequest({
            deckId: this.deck.id,
            deckChanges: new DeckChanges({
                backgroundCardId: this.deckCard.card.item.id!
            })
        });

        this.ponderDataClient
            .deckUpdate(request)
            .pipe(take(1))
            .subscribe({
                next: (response: DataResponse) => {
                    if (response.success) {
                        this.deck.backgroundCardId = this.deckCard.card.item.id!;
                        this.events.deck.deckBackgroundUpdatedEvent.emit(this.deck);
                    }
                }
            })
            .add(() => { this.isProcessing = false });
        
        this.closeDialog();
    }

    // -------------------------------------------------------------------------------------
    // Card Count
    // -------------------------------------------------------------------------------------

    public hasMultiple(): boolean {
        return (this.deckCard.count + this.deckCard.specialCount) > 1;
    }

    public removeAllClicked(): void {
        this.deckComponent.adjustCardCount(this.deckCard, -this.deckCard.count, -this.deckCard.specialCount);
    }

    public swapNormalToFoil(): void {
        this.deckComponent.adjustCardCount(this.deckCard, -this.deckCard.count, this.deckCard.count);
    }

    public swapFoilToNormal(): void {
        this.deckComponent.adjustCardCount(this.deckCard, this.deckCard.specialCount, -this.deckCard.specialCount);
    }

    // -------------------------------------------------------------------------------------
    // Highlight Color
    // -------------------------------------------------------------------------------------
    
    public setHighlightColorClicked(element: HTMLInputElement): void {
        element.click();
    }

    public onHighlightColorChanged(value?: string): void {
        if (this.deckCard.color == value) {
            this.closeDialog();
            return;
        }

        var prevColor = this.deckCard.color;
        this.deckCard.color = value;

        var request = new UpdateDeckRequest({
            deckId: this.deck.id,
            cardChanges: [
                new DeckCardChanges({
                    cardId: this.deckCard.deckCardId,
                    color: value
                })
            ]});

        this.ponderDataClient
            .deckUpdate(request)
            .pipe(take(1))
            .subscribe({
                next: (response: UpdateDeckResponse) => {
                    if (!response.success) {
                        this.deckCard.color = prevColor;
                    }
                },
                error: () => {
                    this.deckCard.color = prevColor
                }
            });
        
        this.closeDialog();
    }

    public clearHighlightColorClicked(): void {
        this.onHighlightColorChanged(undefined);
    }

    // -------------------------------------------------------------------------------------
    // Utility
    // -------------------------------------------------------------------------------------

    public hasNormalVersion(): boolean {
        return this.deckCard.card.item.data?.magic?.treatment?.normal ? true : false;
    }

    public hasSpecialVersion(): boolean {
        return this.deckCard.card.item.data?.magic?.treatment?.special ? true : false;
    }

    public canSwitchToNormal(): boolean {
        return this.hasNormalVersion() && this.deckCard.specialCount > 0;
    }

    public canSwitchToFoil(): boolean {
        return this.hasSpecialVersion() && this.deckCard.count > 0;
    }

    private updateCardTag(to: DeckCardTagType): void {
        var from = this.deckCard.tag;

        if (this.isProcessing || (from == to)) {
            return;
        }
        
        this.isProcessing = true;
        this.deckCard.tag = to;
        this.deckComponent.reloadCard(this.deckCard, this.deckLayerComponent.name);

        var request = new UpdateDeckRequest({
            deckId: this.deck.id,
            cardChanges: [
                new DeckCardChanges({
                    cardId: this.deckCard.deckCardId,
                    tag: to
                })
            ]
        });

        this.ponderDataClient
            .deckUpdate(request)
            .pipe(take(1))
            .subscribe({
                next: (response: DataResponse) => {
                    if (!response.success) {
                        this.deckCard.tag = from;
                        this.deckComponent.reloadCard(this.deckCard, this.deckLayerComponent.name);
                    }
                },
                error: (_: any) => {
                    this.deckCard.tag = from;
                    this.deckComponent.reloadCard(this.deckCard, this.deckLayerComponent.name);
                }
            })
            .add(() => { this.isProcessing = false; });
    }
}